/**
 * Claim-level bulk return-to-review modal.
 * Lets internal request managers select Claims Admin-returned DBQs that are ready to move
 * back into QA review, without reopening each DBQ editor individually.
 */
import React, { useMemo } from 'react';
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
} from 'react-bootstrap';

const DbqBulkReviewReturnModal = ({
  show,
  onHide,
  dbqs = [],
  selectedUserDbqIds = [],
  onToggleUserDbq,
  onSelectAllEligible,
  onClearSelection,
  onSubmit,
  isSubmitting = false,
}) => {
  const selectedDbqs = useMemo(
    () => dbqs.filter((userDbq) => selectedUserDbqIds.includes(userDbq.userDbqId)),
    [dbqs, selectedUserDbqIds],
  );

  const submitLabel = selectedUserDbqIds.length === 1
    ? 'Return 1 DBQ To Review'
    : `Return ${selectedUserDbqIds.length} DBQs To Review`;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem' }}>Return DBQs To Review</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
          Select the Claims Admin-returned DBQs that are ready to move back into QA review.
          Use <strong> Request Changes</strong> when a DBQ still needs to go back to the provider instead.
        </div>

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <div className="small text-muted">
            {dbqs.length} eligible request{dbqs.length !== 1 ? 's' : ''}
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={onSelectAllEligible}
              disabled={isSubmitting || dbqs.length === 0}
            >
              Select all eligible
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onClearSelection}
              disabled={isSubmitting || selectedUserDbqIds.length === 0}
            >
              Clear
            </Button>
          </div>
        </div>

        {dbqs.length === 0 ? (
          <Alert variant="info" className="mb-0">
            There are no DBQ requests on this claim that are currently eligible to return to QA review.
          </Alert>
        ) : (
          <>
            <div className="border rounded overflow-hidden mb-3">
              {dbqs.map((userDbq) => (
                <div
                  key={userDbq.userDbqId}
                  className="d-flex align-items-start gap-3 p-3 border-bottom mb-0"
                  style={{ cursor: isSubmitting ? 'wait' : 'pointer', background: '#fff' }}
                >
                  <Form.Check
                    type="checkbox"
                    className="mt-1"
                    checked={selectedUserDbqIds.includes(userDbq.userDbqId)}
                    disabled={isSubmitting}
                    onChange={(event) => onToggleUserDbq?.(userDbq.userDbqId, event.target.checked)}
                  />
                  <div className="flex-grow-1 min-width-0">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="fw-semibold">
                        {userDbq.conditionTitle || userDbq.title || 'Untitled DBQ'}
                      </div>
                      <Badge
                        bg={userDbq.statusMeta?.variant === 'gray' ? 'light' : (userDbq.statusMeta?.variant || 'secondary')}
                        text={userDbq.statusMeta?.variant === 'gray' ? 'dark' : undefined}
                      >
                        {userDbq.statusMeta?.label || 'Draft'}
                      </Badge>
                    </div>
                    <div className="text-muted small mt-1">
                      {userDbq.title || 'Untitled DBQ'}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded border p-3" style={{ background: '#f8f9fb' }}>
              <div className="fw-semibold mb-2">Selected requests</div>
              {selectedDbqs.length === 0 ? (
                <div className="text-muted small">
                  Select at least one eligible DBQ to return it to review.
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {selectedDbqs.map((userDbq) => (
                    <div key={userDbq.userDbqId}>
                      <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                        {userDbq.title || 'Untitled DBQ'}
                      </div>
                      <div className="text-muted small">
                        {userDbq.conditionTitle || 'No claimed conditions'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={isSubmitting || selectedUserDbqIds.length === 0}
        >
          {isSubmitting ? 'Returning...' : submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DbqBulkReviewReturnModal;
